import {
  AlertFloating,
  DialogTrigger,
  Input,
} from "@light-technology/components";
import { type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { Mail01 } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { api } from "../../state/api";
import { type Role } from "../../state/api.types";
import { errorDetail } from "../../utilities/errors";
import { ROLE_OPTIONS } from "../../utilities/role";
import { emailFieldRules } from "../../utilities/validation";
import { FormModal } from "../shared/FormModal";
import { RoleSelector } from "./RoleSelector";

type InviteTeammateForm = {
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
};

type InviteTeammateModalProps = {
  orgSlug: string;
  children: ReactNode;
};

export const InviteTeammateModal = ({
  orgSlug,
  children,
}: InviteTeammateModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const defaultRole =
    ROLE_OPTIONS.find((opt) => opt.isDefault)?.value ?? ROLE_OPTIONS[0].value;
  const { handleSubmit, control, reset } = useForm<InviteTeammateForm>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      role: defaultRole,
    },
  });

  const [inviteMember, inviteMemberState] = api.useInviteMemberMutation();

  const openModal = () => {
    inviteMemberState.reset();
    reset();
    setIsOpen(true);
  };

  // Auto-close on success
  const [prevIsSuccess, setPrevIsSuccess] = useState(
    inviteMemberState.isSuccess,
  );
  if (prevIsSuccess !== inviteMemberState.isSuccess) {
    setPrevIsSuccess(inviteMemberState.isSuccess);
    if (inviteMemberState.isSuccess) setIsOpen(false);
  }

  const onSubmit = handleSubmit(({ email, firstName, lastName, role }) => {
    inviteMember({
      email,
      first_name: firstName,
      last_name: lastName,
      orgSlug,
      roles: [role],
    });
  });

  const handleOpenChange = (open: boolean) => {
    if (open) openModal();
    else setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      {children}
      <FormModal
        textTitle="Who do you want to invite to your team?"
        textCancel="Cancel"
        textSubmit="Send invite"
        isLoading={inviteMemberState.isLoading}
        onCancel={handleCancel}
        onSubmit={onSubmit}
        data-ph-capture-attribute-action="submit-invite-teammate"
      >
        <div className="mb-2xl flex gap-3xl">
          <Controller
            control={control}
            name="firstName"
            rules={{ required: "First name is required" }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                type="text"
                label="First Name"
                placeholder="First name"
                isRequired
                validationBehavior="aria"
                isInvalid={fieldState.invalid}
                hint={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="lastName"
            rules={{ required: "Last name is required" }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                type="text"
                label="Last Name"
                placeholder="Last name"
                isRequired
                validationBehavior="aria"
                isInvalid={fieldState.invalid}
                hint={fieldState.error?.message}
              />
            )}
          />
        </div>
        <Controller
          control={control}
          name="email"
          rules={emailFieldRules}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              className="mb-2xl"
              type="email"
              label="Email Address"
              placeholder="teammate@example.com"
              icon={Mail01}
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
        <RoleSelector control={control} />
        {inviteMemberState.isError && (
          <div className="mt-xl">
            <AlertFloating
              color="error"
              title="Failed to send invite"
              description={
                errorDetail(inviteMemberState.error as FetchBaseQueryError) ??
                "Failed to send invite. Please try again."
              }
              onClose={() => {
                inviteMemberState.reset();
              }}
              showDismiss={false}
              truncateDescription={false}
            />
          </div>
        )}
      </FormModal>
    </DialogTrigger>
  );
};
